import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth, RequireWorkspace } from '@/components/guards';
import { LoadingPage, NotFoundPage } from '@/components/states';

const page = (loader: () => Promise<{ default: React.ComponentType }>) => {
  const Lazy = React.lazy(loader);
  return (
    <React.Suspense fallback={<LoadingPage />}>
      <Lazy />
    </React.Suspense>
  );
};

export function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={page(() => import('@/pages/login'))} />
        <Route path="/auth/callback" element={page(() => import('@/pages/auth-callback'))} />

        {/* Signed in, workspace not required */}
        <Route element={<RequireAuth />}>
          <Route path="/onboarding" element={page(() => import('@/pages/onboarding'))} />
          <Route path="/oauth/consent" element={page(() => import('@/pages/consent'))} />

          {/* Signed in, inside a workspace */}
          <Route element={<RequireWorkspace />}>
            <Route index element={page(() => import('@/pages/dashboard'))} />

            <Route path="customers" element={page(() => import('@/pages/customers/list'))} />
            <Route path="customers/:id" element={page(() => import('@/pages/customers/detail'))} />

            <Route path="quotes" element={page(() => import('@/pages/quotes/list'))} />
            <Route path="quotes/:id" element={page(() => import('@/pages/quotes/detail'))} />

            <Route path="jobs" element={page(() => import('@/pages/jobs/list'))} />
            <Route path="jobs/:id" element={page(() => import('@/pages/jobs/detail'))} />
            <Route
              path="service-plans"
              element={page(() => import('@/pages/service-plans/list'))}
            />
            <Route
              path="service-plans/:id"
              element={page(() => import('@/pages/service-plans/detail'))}
            />

            <Route path="schedule" element={page(() => import('@/pages/schedule'))} />
            <Route path="today" element={page(() => import('@/pages/today'))} />
            <Route path="today/:id" element={page(() => import('@/pages/field-job'))} />

            <Route path="invoices" element={page(() => import('@/pages/invoices/list'))} />
            <Route path="invoices/:id" element={page(() => import('@/pages/invoices/detail'))} />
            <Route path="invoices/:id/pdf" element={page(() => import('@/pages/invoices/pdf'))} />

            <Route path="payments" element={page(() => import('@/pages/payments'))} />
            <Route path="bank" element={page(() => import('@/pages/bank'))} />
            <Route path="bills" element={page(() => import('@/pages/bills'))} />
            <Route path="expenses" element={page(() => import('@/pages/expenses'))} />
            <Route path="documents" element={page(() => import('@/pages/documents'))} />
            <Route path="operator" element={page(() => import('@/pages/review'))} />
            <Route path="review" element={<Navigate to="/operator" replace />} />
            <Route path="requests" element={page(() => import('@/pages/intake/list'))} />
            <Route path="requests/:id" element={page(() => import('@/pages/intake/detail'))} />

            <Route path="reports" element={page(() => import('@/pages/reports/list'))} />
            <Route path="reports/new" element={page(() => import('@/pages/reports/builder'))} />
            <Route path="reports/:key" element={page(() => import('@/pages/reports/detail'))} />

            <Route path="settings" element={page(() => import('@/pages/settings/workspace'))} />
            <Route
              path="settings/invoice-template"
              element={page(() => import('@/pages/settings/invoice-template'))}
            />
            <Route
              path="settings/payment-reminders"
              element={page(() => import('@/pages/settings/payment-reminders'))}
            />
            <Route
              path="settings/unbilled-work"
              element={page(() => import('@/pages/settings/unbilled-work'))}
            />
            <Route path="settings/bills" element={page(() => import('@/pages/settings/bills'))} />
            <Route path="settings/fields" element={page(() => import('@/pages/settings/fields'))} />
            <Route
              path="settings/relationships"
              element={page(() => import('@/pages/settings/relationships'))}
            />
            <Route
              path="settings/lifecycle-policies"
              element={page(() => import('@/pages/settings/lifecycle-policies'))}
            />
            <Route
              path="settings/field-work"
              element={page(() => import('@/pages/settings/field-work'))}
            />
            <Route
              path="settings/dispatch"
              element={page(() => import('@/pages/settings/dispatch'))}
            />
            <Route
              path="settings/customer-updates"
              element={page(() => import('@/pages/settings/customer-updates'))}
            />
            <Route
              path="settings/request-form"
              element={page(() => import('@/pages/settings/intake'))}
            />
            <Route
              path="settings/lead-response"
              element={page(() => import('@/pages/settings/lead-response'))}
            />
            <Route path="settings/rules" element={page(() => import('@/pages/settings/rules'))} />
            <Route
              path="settings/rules/:key"
              element={page(() => import('@/pages/settings/rule-detail'))}
            />
            <Route
              path="settings/integrations"
              element={page(() => import('@/pages/settings/integrations'))}
            />
            <Route
              path="settings/members"
              element={page(() => import('@/pages/settings/members'))}
            />
            <Route path="settings/audit" element={page(() => import('@/pages/settings/audit'))} />
            <Route
              path="settings/connect"
              element={page(() => import('@/pages/settings/connect'))}
            />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
